import * as React from 'react';

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogProps,
    DialogTitle,
    Grid,
    InputLabel,
    Slider,
    Select,
    MenuItem,
    SvgIcon,
    TextField,
    withStyles,
} from '@material-ui/core';

import {Save, VolumeDown, VolumeMute, VolumeOff, VolumeUp} from '@material-ui/icons';

import {connect} from 'react-redux';
import ITeamState from 'src/redux/modules/team/state';
import {actions as settingsActions} from '../redux/modules/settings';
import {actions as LocalSettingsActions, state as LocalSettingsState} from '../redux/modules/localSettings';
import {isEqual, ISettings} from "../redux/modules/team/types";
import {isInTeam} from "../redux/modules/team/selectors";

const NANOSECOND = 60000000000;

const styles = (theme:any) => ({
    saveIcon: {
        marginLeft: theme.spacing.unit,
    },
    slider: {
        marginTop: "3px",
    },
    volumeIcon: {
        verticalAlign: "middle",
    },
});

const mapStateToProps = (state: { team: ITeamState, localSettings: LocalSettingsState } ) => ({
    localSettings: state.localSettings,
    team: state.team,
});

const mapDispatchToProps = {
    ...LocalSettingsActions,
    ...settingsActions,
};

interface ISettingsDialogState {
    localSettings: LocalSettingsState,
    settings: ISettings,
}

interface ISettingsDialogInternalProps {
    localSettings: LocalSettingsState,
    team: ITeamState,
    saveLocalSettings(localSettings: Partial<LocalSettingsState>):void,
    saveSettings(team:string, settings:object):void,
}

interface ISettingsDialogExternalProps {
    open:boolean;
    classes?:any;

    onClose: (event:React.SyntheticEvent) => void;
}

type Props =  ISettingsDialogInternalProps & ISettingsDialogExternalProps & DialogProps;

class SettingsDialog extends React.Component<Props, ISettingsDialogState> {

    constructor(props:Props) {
        super(props);

        this.state = {
            localSettings: props.localSettings,
            settings: props.team.team!.settings,
        };

        this.handleFocusChange = this.handleFocusChange.bind(this);
        this.handlePauseChange = this.handlePauseChange.bind(this);
        this.handleVolumeChange = this.handleVolumeChange.bind(this);
        this.handleAlarmChange = this.handleAlarmChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    public componentWillReceiveProps(nextProps:Props) {
        if(isInTeam(this.props.team) && !isEqual(this.props.team.team!.settings, nextProps.team.team!.settings)) {
            this.setState({settings: nextProps.team.team!.settings});
        }
    }

    public render() {
        if (this.props.team.fetching || this.props.team.team == null) {
            return null;
        }

        return (
            <Dialog open={ this.props.open } onClose={ this.props.onClose }>
            <form autoComplete='off' onSubmit={ this.handleSubmit }>
                <DialogTitle>
                    Settings
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Here you can set your timers
                    </DialogContentText>
                    <TextField
                        id="focus"
                        name="focus"
                        margin="dense"
                        label="Focus"
                        type="number"
                        fullWidth={ true }
                        required={ true }
                        inputProps={ { min: 1, max: 60 } }
                        value={ this.state.settings.focus / NANOSECOND }
                        onChange={ this.handleFocusChange }
                    />
                    <TextField
                        id="pause"
                        name="pause"
                        margin="dense"
                        label="Pause"
                        type="number"
                        fullWidth={ true }
                        required={ true }
                        inputProps={ { min: 1, max: 60 } }
                        value={ this.state.settings.pause / NANOSECOND }
                        onChange={ this.handlePauseChange }
                    />
                    <Box marginTop="1em">
                        <InputLabel id="volume-slider">Alarm Volume</InputLabel>
                        <Grid container={ true } wrap="nowrap" spacing={ 2 } alignItems="center">
                            <Grid item={ true }>
                                <SvgIcon component={this.getVolumeIcon()} className={ this.props.classes.volumeIcon } />
                            </Grid>
                            <Grid item={ true } xs={ true }>
                                <Slider
                                    min={ 0 }
                                    max={ 100 }
                                    step={ 1 }
                                    className={ this.props.classes.slider }
                                    value={ this.state.localSettings.volume ?? 50 }
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={ this.volumeLabelFormat }
                                    onChange={ this.handleVolumeChange }
                                    aria-labelledby="volume-slider"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    <Box marginTop="1em">
                        <InputLabel id="alarm-sound-label">Alarm Sound</InputLabel>
                        <Select
                            name="alarm"
                            margin="dense"
                            labelId="alarm-sound-label"
                            fullWidth={true}
                            value={this.state.localSettings.alarmSound ?? 'bell.mp3'}
                            onChange={this.handleAlarmChange}
                        >
                            <MenuItem value="bell.mp3">Bell</MenuItem>
                            <MenuItem value="fanfare.mp3">Fanfare</MenuItem>
                            <MenuItem value="schulgong.mp3">Schulgong</MenuItem>
                            <MenuItem value="end.mp3">Ende</MenuItem>
                        </Select>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="primary" type='submit'>
                        Save <Save className={ this.props.classes.saveIcon }/>
                    </Button>
                </DialogActions>
                </form>
            </Dialog>
        );
    }

    private volumeLabelFormat(value: number): React.ReactNode { return `${value}%`; }

    private getVolumeIcon() {
        if (this.state.localSettings.volume > 66) { return VolumeUp; }
        if (this.state.localSettings.volume > 33) { return VolumeDown; }
        if (this.state.localSettings.volume > 0) { return VolumeMute; }
        return VolumeOff;
    }

    private handleFocusChange(event:any) {
        this.setState({settings: { ...this.state.settings, focus: event.currentTarget.value * NANOSECOND }});
    }

    private handlePauseChange(event:any) {
        this.setState({settings: { ...this.state.settings, pause: event.currentTarget.value * NANOSECOND }});
    }

    private handleVolumeChange(event:any, newValue: number) {
        this.setState({localSettings: { ...this.state.localSettings, volume: newValue }});
    }

    private handleAlarmChange(event:any) {
        this.setState({localSettings: { ...this.state.localSettings, alarmSound: event.target.value }});
    }

    private convertToNumber(value: string | number): number {
        if(typeof value === "number") {
            return value;
        }
        if(!isNaN(Number(value))) {
            return Number(value);
        }
        return 0;
    }

    private handleSubmit(event:React.SyntheticEvent) {
        event.preventDefault();

        const focus = this.convertToNumber(this.state.settings.focus);
        const pause = this.convertToNumber(this.state.settings.pause);

        this.props.saveLocalSettings(this.state.localSettings);
        this.props.saveSettings(this.props.team.team!.slug, { focus, pause });
        this.props.onClose(event);
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(SettingsDialog));



// WEBPACK FOOTER //
// ./src/components/SettingsDialog.tsx